/**
 * Story 053 — Invite user dialog.
 *
 * Per UX brief §3.4 — Email + Role (Expert Analyst default) + Welcome
 * message (optional, ≤ 280 chars). The Role select NEVER offers
 * `platform_admin` (BE rejects `role_escalation_forbidden` as the backstop).
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useInviteUserMutation, type Role } from "../queries/index.js";
import { useToast } from "../../account/ToastContext.js";
// SB4-08 — fake-email-domain UX hint. The JSON is a verbatim copy of
// _BACKEND/services/api-gateway/src/clients/auth/fake-email-domains.json
// (the BE is the security boundary; this list drives only the inline
// banner so the user sees the rejection BEFORE submitting). If the two
// files drift, the BE still rejects on POST with 400 fake_email_domain
// and we surface the error message — graceful degradation. A CI lint
// equating the two JSONs is a future polish.
import FAKE_EMAIL_DOMAINS_RAW from "./fake-email-domains.json";
const FAKE_EMAIL_DOMAINS: ReadonlySet<string> = new Set(
  (FAKE_EMAIL_DOMAINS_RAW as readonly string[]).map((s) => s.toLowerCase()),
);

const WELCOME_MAX = 280;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function extractDomain(email: string): string {
  return email.split("@")[1]?.toLowerCase().trim() ?? "";
}

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InviteUserDialog({
  open,
  onClose,
}: InviteUserDialogProps): ReactElement {
  const { toast } = useToast();
  const invite = useInviteUserMutation();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("expert_analyst");
  const [welcome, setWelcome] = useState("");
  const [error, setError] = useState<string | null>(null);

  // SB4-08 — non-blocking warning when the entered domain is on the
  // fake-domain deny-list. Computed reactively so the banner appears
  // as soon as the user finishes typing `@<bad-domain>`. Submission
  // is also blocked client-side; the BE re-checks server-side.
  const emailDomain = extractDomain(email);
  const isFakeDomain =
    emailDomain.length > 0 && FAKE_EMAIL_DOMAINS.has(emailDomain);
  const isValidShape = EMAIL_RE.test(email);
  const canSubmit = isValidShape && !isFakeDomain && !invite.isPending;

  function reset(): void {
    setEmail("");
    setRole("expert_analyst");
    setWelcome("");
    setError(null);
  }

  function close(): void {
    reset();
    onClose();
  }

  async function submit(): Promise<void> {
    setError(null);
    if (!EMAIL_RE.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (isFakeDomain) {
      // Defence in depth: the canSubmit guard already disables the
      // button, but a stale state path could still reach here.
      setError(
        `The domain "${emailDomain}" is a placeholder / disposable address. Invitations sent there will bounce.`,
      );
      return;
    }
    try {
      const body = welcome.trim()
        ? { email, role, welcomeMessage: welcome.trim() }
        : { email, role };
      await invite.mutateAsync(body);
      toast(`Invitation sent to ${email}.`);
      close();
    } catch (e) {
      const status = (e as { status?: number }).status;
      const reason = (e as { body?: { reason?: string } }).body?.reason;
      // SB4-08 — the BE returns 400 with `code: 'fake_email_domain'`
      // and a `domain` field. If the FE deny-list drifted out of sync
      // with the BE, the BE's rejection still surfaces a clear inline
      // message rather than the generic catch-all below.
      const code = (e as { body?: { code?: string } }).body?.code;
      const domain = (e as { body?: { domain?: string } }).body?.domain;
      if (status === 400 && code === "fake_email_domain") {
        setError(
          domain
            ? `The domain "${domain}" is not allowed for tenant invitations.`
            : "Invitations to placeholder email domains are not allowed.",
        );
        return;
      }
      if (status === 409 && reason === "email_already_exists") {
        setError("A user with that email already exists in this tenant.");
      } else if (status === 403 && reason === "role_escalation_forbidden") {
        setError("Only a Platform Admin can grant Platform Admin role.");
      } else {
        setError("Couldn't send invitation. Try again.");
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      role="dialog"
      aria-labelledby="invite-user-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="invite-user-dialog-title">Invite user</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            We&apos;ll send them an email with a link to set their password and
            join your tenant.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            fullWidth
            required
            autoComplete="email"
            error={isFakeDomain}
            helperText={
              isFakeDomain
                ? `"${emailDomain}" looks like a placeholder / disposable email domain.`
                : undefined
            }
          />
          {/* SB4-08 — non-blocking banner when the entered email
              matches the fake-domain deny-list. Mirrors the inline
              helperText on the field but surfaces above the form so
              it can't be missed at a glance. */}
          {isFakeDomain ? (
            <Alert
              role="status"
              severity="warning"
              data-testid="invite-fake-domain-warning"
            >
              The domain <strong>{emailDomain}</strong> is a placeholder /
              disposable address. Invitations sent there will bounce; pick a
              real email.
            </Alert>
          ) : null}
          {/* Story 054a — dropdown widened to include project_manager
              and back_office. Platform Admin is NOT in the list — the
              BE rejects role_escalation_forbidden as the backstop
              (re-asserted by Story 053 AC9 and 054a AC8).
              MUI's listbox-style select (rather than native:true) so
              the dropdown options expose `role="option"` with a
              non-zero bounding rect for `toBeVisible()` assertions
              (AC7). */}
          <TextField
            label="Role"
            select
            value={role}
            onChange={(e) => {
              setRole(e.target.value as Role);
            }}
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
            }}
          >
            <MenuItem value="expert_analyst">Expert Analyst</MenuItem>
            <MenuItem value="pre_screener">Pre-Screener</MenuItem>
            <MenuItem value="project_manager">Project Manager</MenuItem>
            <MenuItem value="back_office">Back Office</MenuItem>
            <MenuItem value="pilot">Pilot</MenuItem>
            <MenuItem value="tenant_admin">Tenant Admin</MenuItem>
          </TextField>
          <TextField
            label="Welcome message"
            multiline
            minRows={3}
            maxRows={6}
            value={welcome}
            onChange={(e) => {
              setWelcome(e.target.value.slice(0, WELCOME_MAX));
            }}
            helperText={`${String(welcome.length)} / ${String(WELCOME_MAX)} characters`}
            fullWidth
          />
          {error ? (
            <Alert role="alert" severity="error">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            void submit();
          }}
          disabled={!canSubmit}
          startIcon={
            invite.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Send invite
        </Button>
      </DialogActions>
    </Dialog>
  );
}
